import { nextTick } from "vue";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyStore } from "@stores/survey/survey";

const SETTLE_TIMEOUT_MS = 15000;
const SETTLE_POLL_MS = 150;
const TEARDOWN_FALLBACK_MS = 1000;
const PRINTED_PAGE_HEIGHT_PX = 1000;

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
const waitUntil = async (condition: () => boolean) => {
  const deadline = Date.now() + SETTLE_TIMEOUT_MS;
  while (!condition() && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_MS));
  }
};

export const usePrintDashboard = () => {
  const store = useResultsV2Store();
  const surveyStore = useSurveyStore();

  const enterPrintMode = () => {
    const wasEditMode = store.editMode;
    const previousTitle = document.title;
    let restored = false;

    const restore = () => {
      if (restored) return;
      restored = true;
      window.removeEventListener("afterprint", restore);
      document.title = previousTitle;
      store.printing = false;
      store.printTallCardIds = new Set();
      if (wasEditMode) store.setEditMode(true);
    };

    if (wasEditMode) store.setEditMode(false);
    store.printing = true;
    window.addEventListener("afterprint", restore);
    return restore;
  };

  const cardList = () => document.querySelector("[data-results-card-list]");

  const measureTallCards = () => {
    store.printTallCardIds = new Set(
      [...(cardList()?.querySelectorAll<HTMLElement>("[data-results-card]") || [])]
        .filter((card) => card.offsetHeight > PRINTED_PAGE_HEIGHT_PX)
        .map((card) => Number(card.id.replace("results-card-", ""))),
    );
  };

  const setPrintTitle = () => {
    document.title = [surveyStore.survey?.name, store.viewName, new Date().toISOString().slice(0, 10)]
      .filter(Boolean)
      .join(" - ");
  };

  const printDashboard = async () => {
    if (store.printing) return;
    const restore = enterPrintMode();

    try {
      await waitUntil(() => !store.loading);
      await nextTick();
      await document.fonts.ready;
      await waitUntil(() => !cardList()?.querySelector(".animate-spin"));
      await nextFrame();
      measureTallCards();
      await nextFrame();
      setPrintTitle();
      window.print();
      setTimeout(restore, TEARDOWN_FALLBACK_MS);
    } catch (error) {
      restore();
      throw error;
    }
  };

  const handleBeforePrint = () => {
    if (store.printing) return;
    enterPrintMode();
    setPrintTitle();
    nextTick().then(measureTallCards);
  };

  return { printDashboard, handleBeforePrint };
};
